import React, { FC, memo, useCallback, useState } from 'react';
import { Image } from 'antd';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { useRole, getUserName } from 'entities/User';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { VerificationDocument, VerificationDocumentType, VerificationFileStatus } from 'entities/VerificationPhoto';
import { Button } from 'shared/ui/Button';
import { DocumentCard } from './DocumentCard';
import { ManageVerificationButtons } from './ManageVerificationButtons';
import { getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';

interface LocalFilePreview {
  file: File;
  preview: string;
}

interface UploadDocumentProps {
  documents?: Nullable<VerificationDocument[]>;
  documentFields?: Array<{
    key: VerificationDocumentType;
    label: string;
  }>;
  onSuccess?: (localFiles: Record<string, LocalFilePreview | null>) => void;
  accept?: string;
}

export const UploadDocument: FC<UploadDocumentProps> = memo((props) => {
  const { documents, documentFields = [], onSuccess, accept = '.jpg,.jpeg,.png,.heic,.webp' } = props;

  const { t } = useAppTranslation('common');
  const { isManagement, isCustomer } = useRole();
  const { isDesktop, isMobile } = useScreenBreakpoints();

  const [localFiles, setLocalFiles] = useState<Record<string, LocalFilePreview | null>>({});
  const [previewVisibleMap, setPreviewVisibleMap] = useState<Record<string, boolean>>({});

  const isDocRequiredFieldsType = Boolean(documentFields.length);

  const handleFileSelect = async (key: string, fileList: FileList | null): Promise<void> => {
    if (!fileList?.[0]) {
      return;
    }

    const file = fileList[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setLocalFiles((prev) => ({
        ...prev,
        [key]: { file, preview: reader.result as string },
      }));
    };

    reader.readAsDataURL(file);
  };

  const getDocument = useCallback(
    (type: string): VerificationDocument | undefined => documents?.find((d) => d.documentType === type),
    [documents],
  );

  const renderPreview = useCallback(
    (key: string, doc?: VerificationDocument): React.ReactElement => {
      const local = localFiles[key];
      const isRejected = doc?.fileStatus === VerificationFileStatus.REJECTED;
      const isApproved = doc?.fileStatus === VerificationFileStatus.APPROVED;
      const isUploaded = Boolean(doc?.base64);

      const previewSrc = local?.preview ? local.preview : isUploaded ? doc?.base64 : doc?.fileUrl;

      if (previewSrc) {
        const isVisible = previewVisibleMap[key] || false;

        const canUploadNewDoc = isDocRequiredFieldsType && (isManagement || (!isApproved && (!isCustomer || isRejected)));

        return (
          <>
            <Image
              height={isMobile ? 180 : 200}
              width="100%"
              className="rounded-lg object-cover"
              src={previewSrc}
              preview={{
                visible: isVisible,
                scaleStep: 3,
                src: previewSrc,
                onVisibleChange: (value) => {
                  setPreviewVisibleMap((prev) => ({
                    ...prev,
                    [key]: value,
                  }));
                },
              }}
            />

            {doc?.uploadDate && (
              <div className="text-primaryLight italic text-sm">
                {t('Uploaded')}: {getClientDateWithMinuteTimeFormat(doc.uploadDate)}
              </div>
            )}

            {(isApproved || isRejected) && doc?.reviewedBy && isManagement && (
              <div className="text-primaryLight italic text-sm">
                {t(isApproved ? 'Approved by {{name}} on {{date}}' : 'Rejected by {{name}} on {{date}}', {
                  name: getUserName(doc.reviewedBy),
                  date: doc.reviewedAt ? getClientDateWithMinuteTimeFormat(doc.reviewedAt) : '',
                })}
              </div>
            )}

            {isRejected && (
              <div>
                <div className="text-sm text-error mt-2">
                  <p>{t('Document is rejected')}</p>
                  {doc?.rejectReason && (
                    <div className="flex justify-center text-sm text-primaryLight italic mt-1">{t(doc.rejectReason)}</div>
                  )}
                </div>

                {doc.note && <div className="mt-2 italic bg-errorLight border border-error rounded-md p-2">{doc.note || ''}</div>}
              </div>
            )}

            {canUploadNewDoc && (
              <label className="cursor-pointer text-success mt-3 underline text-sm">
                {local ? t('Replace file') : t('Upload new file')}
                <input
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={async (e) => {
                    await handleFileSelect(key, e.target.files);
                  }}
                />
              </label>
            )}
          </>
        );
      }

      return (
        <label
          className="flex flex-col items-center justify-center cursor-pointer w-full h-full"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={async (e) => {
            e.preventDefault();
            await handleFileSelect(key, e.dataTransfer.files);
          }}
        >
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={async (e) => {
              await handleFileSelect(key, e.target.files);
            }}
          />

          <div className="text-gray-400 text-sm mt-2">{t('Click or drag file', { ns: 'common' })}</div>
        </label>
      );
    },
    [accept, isCustomer, isDocRequiredFieldsType, isManagement, isMobile, localFiles, previewVisibleMap, t],
  );

  return (
    <div className="space-y-5">
      <div className={`grid gap-4 mt-4 ${isDesktop ? 'grid-cols-3' : 'grid-cols-1'}`}>
        {isDocRequiredFieldsType &&
          documentFields.map(({ key, label }) => {
            const doc = getDocument(key);

            return (
              <DocumentCard key={key} label={label} status={doc?.fileStatus}>
                {renderPreview(key, doc)}

                {isManagement && doc?.verificationPhotoId && (
                  <div className="mt-4 flex flex-col gap-2 w-full h-full justify-end">
                    <ManageVerificationButtons
                      verificationPhotoId={doc.verificationPhotoId}
                      fileStatus={doc.fileStatus}
                      documentType={doc.documentType}
                    />
                  </div>
                )}
              </DocumentCard>
            );
          })}

        {!isDocRequiredFieldsType &&
          documents?.map((doc) => (
            <DocumentCard key={doc.verificationPhotoId} status={doc.fileStatus}>
              {renderPreview(doc.verificationPhotoId, doc)}

              {doc?.verificationPhotoId && (
                <div className="mt-4 flex flex-col gap-2 w-full h-full justify-end">
                  <ManageVerificationButtons
                    verificationPhotoId={doc.verificationPhotoId}
                    fileStatus={doc.fileStatus}
                    contractId={doc?.contractId}
                    documentType={doc.documentType}
                  />
                </div>
              )}
            </DocumentCard>
          ))}
      </div>

      <div className="flex justify-end mt-4">
        <Button
          onClick={() => {
            onSuccess?.(localFiles);
          }}
          className="mt-4"
          containerClassName="w-full desktop:w-[30%]"
        >
          {t('Done', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
});
